/* eslint-disable */
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { operationsApi } from "./api";
import useModalA11y from "../utils/useModalA11y";
import "./StaffDialogs.css";

export default function AddStaffDialog({ onClose, onSaved, roleScope = "all" }) {
  const modalRef = useModalA11y({ isOpen: true, onClose });
  const [roles, setRoles] = useState([]);
  // The security incharge may only add line guards.
  const guardsOnly = roleScope === "guards_only";
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    alt_phone: "",
    role: "SECURITY_GUARD",
    notes: "",
    employed_since: "",
    user_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    operationsApi
      .listRoles()
      .then((rs) =>
        setRoles(guardsOnly ? rs.filter((r) => r.value === "SECURITY_GUARD") : rs)
      )
      .catch(() => {});
  }, [guardsOnly]);

  const isFm = form.role === "FACILITY_MANAGER";

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.full_name.trim()) {
      setError("Name is required");
      return;
    }
    const digits = form.phone.replace(/\D/g, "").slice(-10);
    if (digits.length !== 10) {
      setError("Phone must be a 10-digit Indian number");
      return;
    }
    if (isFm && !form.user_id.trim()) {
      setError(
        "Facility Manager must be linked to an existing user account."
      );
      return;
    }
    setBusy(true);
    setError("");
    try {
      await operationsApi.addStaff({
        full_name: form.full_name.trim(),
        phone: digits,
        alt_phone: form.alt_phone.trim() || null,
        role: form.role,
        notes: form.notes.trim() || null,
        employed_since: form.employed_since || null,
        user_id: form.user_id.trim() || null,
      });
      onSaved && onSaved();
    } catch (err) {
      setError(err.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vsop-overlay" onClick={onClose}>
      <form
        ref={modalRef}
        className="vsop-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vsop-add-title"
      >
        <div className="vsop-modal-head">
          <h2 id="vsop-add-title">Add staff member</h2>
          <button
            type="button"
            className="vsop-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="vsop-modal-field">
          <label htmlFor="vsop-add-name">Full name</label>
          <input
            id="vsop-add-name"
            type="text"
            value={form.full_name}
            onChange={(e) => update("full_name", e.target.value)}
            placeholder="Staff member's full name"
            required
            maxLength={255}
          />
        </div>

        <div className="vsop-modal-field">
          <label htmlFor="vsop-add-role">Role</label>
          <select
            id="vsop-add-role"
            value={form.role}
            onChange={(e) => update("role", e.target.value)}
          >
            {roles.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        <div className="vsop-modal-row">
          <div className="vsop-modal-field">
            <label htmlFor="vsop-add-phone">Phone</label>
            <input
              id="vsop-add-phone"
              type="tel"
              inputMode="numeric"
              value={form.phone}
              onChange={(e) =>
                update("phone", e.target.value.replace(/\D/g, "").slice(0, 10))
              }
              placeholder="9876543210"
              required
              maxLength={10}
            />
          </div>
          <div className="vsop-modal-field">
            <label htmlFor="vsop-add-alt">Alt phone (optional)</label>
            <input
              id="vsop-add-alt"
              type="tel"
              inputMode="numeric"
              value={form.alt_phone}
              onChange={(e) =>
                update(
                  "alt_phone",
                  e.target.value.replace(/\D/g, "").slice(0, 10)
                )
              }
              maxLength={10}
            />
          </div>
        </div>

        {isFm && (
          <div className="vsop-modal-field">
            <label htmlFor="vsop-add-user">User ID (must exist)</label>
            <input
              id="vsop-add-user"
              type="text"
              value={form.user_id}
              onChange={(e) => update("user_id", e.target.value)}
              placeholder="UUID of the FM's user account"
              required
            />
            <p className="vsop-modal-helper">
              The Facility Manager logs in to the app to manage the
              duty roster, so they need a regular user account first.
            </p>
          </div>
        )}

        <div className="vsop-modal-field">
          <label htmlFor="vsop-add-since">Employed since (optional)</label>
          <input
            id="vsop-add-since"
            type="date"
            value={form.employed_since}
            onChange={(e) => update("employed_since", e.target.value)}
          />
        </div>

        <div className="vsop-modal-field">
          <label htmlFor="vsop-add-notes">Notes (optional)</label>
          <textarea
            id="vsop-add-notes"
            rows={2}
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            placeholder="Speaks Telugu only · lives on-site at Gate 2"
            maxLength={2000}
          />
        </div>

        {error && <div className="vsop-modal-error">{error}</div>}

        <div className="vsop-modal-actions">
          <button
            type="button"
            className="vsop-btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="vsop-btn-primary"
            disabled={busy}
          >
            {busy ? "Saving…" : "Add staff member"}
          </button>
        </div>
      </form>
    </div>
  );
}
